import React, { useRef, useState } from 'react';
import './LandingPage.css';
import { useNavigate } from 'react-router-dom';
import { FaApple, FaGooglePlay, FaLinkedin, FaArrowRight } from 'react-icons/fa';

const PRODUCTS = [
  {
    num: '01',
    key: 'rivalcam',
    title: 'RivalCam',
    badge: 'Live',
    tagline: 'Fan-versus-fan challenges, live.',
    desc: 'Real-time rivalry pipelines with live scoring, trophy cams, and crowd-sourced clips — all cryptographically verified.',
    image: '/images/img1/rival.jpg',
    ctas: [
      { label: 'See rivalries', action: (nav) => nav('/app/rivalry'), variant: 'solid' },
      { label: 'Fan beta reels', action: (nav) => nav('/app/chat'), variant: 'ghost' },
    ],
  },
  {
    num: '02',
    key: 'smart-amenity',
    title: 'Smart Amenity',
    badge: 'New',
    tagline: 'Frictionless property access.',
    desc: 'Residents tap in once. Property teams get live occupancy, guest passes, and waivers — no front-desk friction.',
    image: '/images/img1/pool.png',
    ctas: [
      { label: 'Book a demo', href: 'https://smartamenity.net', variant: 'solid' },
    ],
  },
  {
    num: '03',
    key: 'content-tamper',
    title: 'ContentTamper',
    badge: 'Private Beta',
    tagline: 'Proof of creative work.',
    desc: 'Lock files, see who touched what, and export tamper-evident proof right alongside your footage.',
    image: '/images/img1/contentTamper.jpg',
    ctas: [
      { label: 'Join waitlist', href: 'mailto:services@meanc.net?subject=ContentTamper%20Waitlist', variant: 'solid' },
    ],
  },
  {
    num: '04',
    key: 'env-guard',
    title: 'EnvGuard',
    badge: 'Security',
    tagline: 'Clean environments, safer engineers.',
    desc: 'Rotate secrets, monitor API usage, and keep developer environments tidy without slowing the team down.',
    image: '/images/img1/enguard.jpg',
    ctas: [
      { label: 'Talk to security', href: 'mailto:services@meanc.net?subject=EnvGuard%20Consult', variant: 'solid' },
    ],
  },
];

const STATS = [
  { value: '2022', label: 'Founded' },
  { value: '4', label: 'Product lines' },
  { value: '10k+', label: 'Fans served' },
  { value: '99.9%', label: 'Uptime target' },
];

const VALUES = [
  { icon: '⚡', title: 'Performance', desc: 'Realtime pipelines tuned for milliseconds.' },
  { icon: '🔐', title: 'Security', desc: 'Zero-trust content protection across every upload.' },
  { icon: '🎨', title: 'Craft', desc: 'Motion-forward UX that explains value in seconds.' },
  { icon: '🤝', title: 'Service', desc: 'White-glove launches, support, and migration.' },
];

const heroBackdrop = '/images/img1/thomas-serer-alUSb8lCE5s-unsplash.jpg';
const innovationBackdrop = '/images/img1/fans.jpg';

export default function LandingPage() {
  const navigate = useNavigate();
  const contactRef = useRef(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', topic: '', message: '' });
  const [contactStatus, setContactStatus] = useState('');

  const scrollToContact = () =>
    contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const { name, email, topic, message } = contactForm;
    if (!name.trim() || !email.trim() || !message.trim()) {
      setContactStatus('Please add your name, email, and a quick message.');
      return;
    }
    const subject = encodeURIComponent(`MEANC contact: ${topic || 'General inquiry'}`);
    const body = encodeURIComponent(
      [`Name: ${name}`, `Email: ${email}`, topic ? `Topic: ${topic}` : null, '', message]
        .filter(Boolean)
        .join('\n')
    );
    window.location.href = `mailto:services@meanc.net?subject=${subject}&body=${body}`;
    setContactStatus('Opening your email client…');
  };

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section
        className="lp-hero"
        style={{
          backgroundImage: `
            radial-gradient(ellipse 70% 55% at 75% 15%, rgba(93,220,255,0.11), transparent),
            radial-gradient(ellipse 50% 45% at 5% 85%, rgba(242,54,138,0.07), transparent),
            linear-gradient(165deg, rgba(3,8,20,0.97) 0%, rgba(5,11,32,0.93) 60%, rgba(10,18,49,0.90) 100%),
            url(${heroBackdrop})`,
        }}
      >
        <div className="lp-hero__inner">
          <p className="lp-eyebrow">MEANC LLC &mdash; Software Company</p>
          <h1 className="lp-hero__heading">
            Software for fans,<br className="lp-hero__br" />
            buildings &amp;&nbsp;creators.
          </h1>
          <p className="lp-hero__sub">
            Four focused products that do different jobs: hype fans, smooth building
            access, protect creative work, and keep engineering tidy. Built to hold up
            when traffic surges.
          </p>

          <div className="lp-hero__tags" aria-label="Key capabilities">
            {['Realtime video', 'Zero-trust content', 'Smart amenities', 'Developer console'].map((t) => (
              <span key={t}>{t}</span>
            ))}
          </div>

          <div className="lp-hero__ctas">
            <button className="lp-btn lp-btn--primary" onClick={() => navigate('/access-lab')}>
              Launch Console <FaArrowRight aria-hidden="true" />
            </button>
            <button className="lp-btn lp-btn--ghost" onClick={scrollToContact}>
              Talk to MEANC
            </button>
          </div>

          <div className="lp-hero__store">
            <button className="lp-store-pill" disabled aria-label="App Store — coming soon">
              <FaApple aria-hidden="true" />
              <span>App Store &mdash; coming soon</span>
            </button>
            <button className="lp-store-pill" disabled aria-label="Google Play — coming soon">
              <FaGooglePlay aria-hidden="true" />
              <span>Google Play &mdash; coming soon</span>
            </button>
          </div>
        </div>

        <div className="lp-hero__scroll-hint" aria-hidden="true">
          <span />
        </div>
      </section>

      {/* ── Stats band ───────────────────────────────────────────────── */}
      <div className="lp-stats" role="list" aria-label="Company metrics">
        {STATS.map((s) => (
          <div key={s.label} className="lp-stats__item" role="listitem">
            <strong>{s.value}</strong>
            <span>{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── Products ─────────────────────────────────────────────────── */}
      <section className="lp-products" id="products" aria-labelledby="products-heading">
        <div className="lp-section-head">
          <p className="lp-eyebrow lp-eyebrow--dark">MEANC product line</p>
          <h2 id="products-heading">Four products, four clear jobs</h2>
          <p className="lp-section-sub">
            Each product is narrow on purpose — easy to understand, fast to adopt,
            and engineered to stay steady under load.
          </p>
        </div>

        <div className="lp-products__grid">
          {PRODUCTS.map((p) => (
            <article key={p.key} className={`lp-card lp-card--${p.key}`}>
              <span className="lp-card__num" aria-hidden="true">{p.num}</span>
              <figure className="lp-card__fig">
                <img src={p.image} alt={`${p.title} — ${p.tagline}`} loading="lazy" />
              </figure>
              <div className="lp-card__body">
                <div className="lp-card__meta">
                  <span className="lp-badge">{p.badge}</span>
                </div>
                <h3 className="lp-card__title">{p.title}</h3>
                <p className="lp-card__tagline">{p.tagline}</p>
                <p className="lp-card__desc">{p.desc}</p>
                <div className="lp-card__actions">
                  {p.ctas.map((cta) =>
                    cta.href ? (
                      <a
                        key={cta.label}
                        className={`lp-btn lp-btn--sm ${cta.variant === 'ghost' ? 'lp-btn--card-ghost' : 'lp-btn--card'}`}
                        href={cta.href}
                        target={cta.href.startsWith('mailto') ? undefined : '_blank'}
                        rel="noreferrer noopener"
                      >
                        {cta.label}
                      </a>
                    ) : (
                      <button
                        key={cta.label}
                        className={`lp-btn lp-btn--sm ${cta.variant === 'ghost' ? 'lp-btn--card-ghost' : 'lp-btn--card'}`}
                        type="button"
                        onClick={() => cta.action(navigate)}
                      >
                        {cta.label}
                      </button>
                    )
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── ContentTamper banner ──────────────────────────────────────── */}
      <section
        className="lp-banner"
        style={{
          backgroundImage: `linear-gradient(140deg, rgba(3,8,20,0.92) 0%, rgba(12,20,55,0.80) 100%), url(${innovationBackdrop})`,
        }}
        aria-labelledby="banner-heading"
      >
        <div className="lp-banner__inner">
          <p className="lp-eyebrow">Coming soon</p>
          <h2 id="banner-heading">Security storytelling<br />with ContentTamper</h2>
          <p>
            We are polishing an illustrated security layer that keeps creators in control
            of their footage from the first upload through verified handoff.
          </p>
          <a
            className="lp-btn lp-btn--primary"
            href="mailto:services@meanc.net?subject=ContentTamper%20Waitlist"
          >
            Join the waitlist <FaArrowRight aria-hidden="true" />
          </a>
        </div>
      </section>

      {/* ── Smart Amenity ────────────────────────────────────────────── */}
      <section className="lp-amenity" id="smart-amenity" aria-labelledby="amenity-heading">
        <div className="lp-amenity__copy">
          <p className="lp-eyebrow lp-eyebrow--dark">Smart Amenity</p>
          <h2 id="amenity-heading">Frictionless, data-aware amenities for modern properties</h2>
          <p>
            Residents tap in once and move through the gym, pool, or lounge without
            front-desk friction. Property teams see live occupancy, manage guest passes,
            and keep waivers in one place.
          </p>
          <div className="lp-amenity__chips">
            {['Live occupancy', 'Guest passes', 'Access control', 'Resident identity'].map((c) => (
              <span key={c}>{c}</span>
            ))}
          </div>
          <button
            className="lp-btn lp-btn--primary"
            onClick={() => window.open('https://smartamenity.net', '_blank', 'noopener,noreferrer')}
          >
            Open smartamenity.net <FaArrowRight aria-hidden="true" />
          </button>
        </div>
        <div className="lp-amenity__visual">
          <div className="lp-feature-card">
            <img src="/images/img1/pool.png" alt="Smart Amenity — pool access for residents" loading="lazy" />
            <div className="lp-feature-card__meta">
              <span className="lp-badge lp-badge--cyan">New</span>
              <p>Premium experiences without front-desk friction.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── About ────────────────────────────────────────────────────── */}
      <section className="lp-about" id="about" aria-labelledby="about-heading">
        <div className="lp-about__copy">
          <p className="lp-eyebrow">About MEANC LLC</p>
          <h2 id="about-heading">Built to perform<br />under pressure</h2>
          <p className="lp-about__text">
            MEANC LLC builds a small set of products that do different jobs: hype fans,
            smooth building access, protect creative work, and keep engineering tidy.
            We pair vivid interfaces with grounded engineering so partners see value fast
            and stay confident when they scale.
          </p>

          <div className="lp-founder">
            <div className="lp-founder__head">
              <img
                src="/images/img1/mosesPhoto.png"
                alt="Moussa Diallo — Founder and CEO of MEANC LLC"
                loading="lazy"
              />
              <div>
                <p className="lp-founder__role">Founder &amp; CEO</p>
                <h3>Moussa Diallo</h3>
              </div>
            </div>
            <p className="lp-founder__bio">
              I started MEANC to ship software that performs under pressure — whether
              that is a packed stadium or a busy property lobby. I stay close to the
              work: designing flows, reviewing code, and onboarding partners so every
              product feels deliberate and reliable.
            </p>
            <a
              className="lp-btn lp-btn--linkedin"
              href="https://www.linkedin.com/in/mosesdm/"
              target="_blank"
              rel="noreferrer noopener"
            >
              <FaLinkedin aria-hidden="true" /> Connect on LinkedIn
            </a>
          </div>

          <div className="lp-values" aria-label="Company values">
            {VALUES.map((v) => (
              <div key={v.title} className="lp-values__item">
                <span className="lp-values__icon" aria-hidden="true">{v.icon}</span>
                <div>
                  <h4>{v.title}</h4>
                  <p>{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lp-about__visual">
          <img
            src="https://images.unsplash.com/photo-1487014679447-9f8336841d58?auto=format&fit=crop&w=1200&q=80"
            alt="Software development and collaboration"
            loading="lazy"
          />
          <div className="lp-about__metrics" aria-label="Company milestones">
            <div><strong>2022</strong><span>Founded</span></div>
            <div><strong>4</strong><span>Product lines</span></div>
            <div><strong>&#8734;</strong><span>Live fan moments</span></div>
          </div>
        </div>
      </section>

      {/* ── Contact ──────────────────────────────────────────────────── */}
      <section className="lp-contact" id="contact" ref={contactRef} aria-labelledby="contact-heading">
        <div className="lp-contact__copy">
          <p className="lp-eyebrow lp-eyebrow--dark">Talk to MEANC</p>
          <h2 id="contact-heading">Tell us about your launch, property, or security needs</h2>
          <p>
            Share a quick note and we will follow up with the right team — fan engagement,
            smart amenities, or security engineering.
          </p>
          <ul className="lp-contact__list">
            <li>Response within 1 business day</li>
            <li>White-glove onboarding for partners</li>
            <li>Privacy-first: we only use your details to reply</li>
          </ul>
        </div>

        <div className="lp-contact__form-wrap">
          <form className="lp-contact__form" onSubmit={handleContactSubmit} noValidate>
            <label>
              Full name
              <input
                type="text"
                name="name"
                placeholder="Your name"
                value={contactForm.name}
                onChange={handleContactChange}
                autoComplete="name"
                required
              />
            </label>
            <label>
              Work email
              <input
                type="email"
                name="email"
                placeholder="you@company.com"
                value={contactForm.email}
                onChange={handleContactChange}
                autoComplete="email"
                required
              />
            </label>
            <label>
              Topic
              <select name="topic" value={contactForm.topic} onChange={handleContactChange}>
                <option value="">Select a topic</option>
                <option value="RivalCam">RivalCam / live fan apps</option>
                <option value="Smart Amenity">Smart Amenity / property tech</option>
                <option value="ContentTamper">ContentTamper / creator security</option>
                <option value="EnvGuard">EnvGuard / secrets &amp; APIs</option>
                <option value="Partnerships">Partnerships / other</option>
              </select>
            </label>
            <label>
              Message
              <textarea
                name="message"
                rows="4"
                placeholder="What would you like to build or learn about?"
                value={contactForm.message}
                onChange={handleContactChange}
                required
              />
            </label>
            <p className="lp-contact__hint">
              Submitting opens your email client to send directly to services@meanc.net.
            </p>
            {contactStatus && <p className="lp-contact__status" role="status">{contactStatus}</p>}
            <button type="submit" className="lp-btn lp-btn--primary lp-btn--full">
              Send message <FaArrowRight aria-hidden="true" />
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
